// Szerkesztő-útvonal. A cikk azonosító (nem slug) szerint töltődik: a szerkesztés a resource-t
// célozza, nem az útvonalát — így a slug/kategória átmozgatása sem töri el a nyitott szerkesztést.
import { useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppShell } from '@/components/shared/app-shell';
import { ReadOnlyNotice } from '@/components/shared/read-only-notice';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useDeleteDraft, useEditorArticle, useEditorDraft } from '@/features/editor/api';
import { editorKeys } from '@/features/editor/api';
import { ArticleEditor } from '@/features/editor/components/article-editor';
import { ArticlePathCrumbs } from '@/features/editor/components/article-path-crumbs';
import { LifecycleBar } from '@/features/editor/components/lifecycle-bar';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';

export const Route = createFileRoute('/szerkesztes/$articleId')({
  beforeLoad: ({ location }) => requireAuth(location.href),
  component: EditorPage,
});

function EditorPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { articleId } = Route.useParams();

  const queryClient = useQueryClient();
  const session = api.session.useQuery(['session']);
  const articleQuery = useEditorArticle(articleId);
  const draftQuery = useEditorDraft(articleId);
  const deleteDraft = useDeleteDraft();

  /** `null`: még nem döntött a vázlatról; `undefined`: eldobta; objektum: betöltötte. */
  const [draftChoice, setDraftChoice] = useState<{ title: string; body: string } | null | undefined>(
    null,
  );

  const sessionBody = session.data?.status === 200 ? session.data.body : null;

  if (articleQuery.isLoading) {
    return (
      <AppShell session={sessionBody}>
        <div className="space-y-4">
          <Skeleton className="h-10 w-1/2" />
          <Skeleton className="h-96 w-full" />
        </div>
      </AppShell>
    );
  }

  const article = articleQuery.data?.status === 200 ? articleQuery.data.body : null;
  if (!article) {
    // 404 (nincs ilyen cikk) és 403 (nincs szerkesztési jog) — külön üzenet jár a felhasználónak.
    const status = (articleQuery.data as { status?: number } | undefined)?.status;
    return (
      <AppShell session={sessionBody}>
        <div className="py-12 text-center">
          <p className="text-muted-foreground">
            {status === 404 ? t('editor.notFound') : t('editor.loadError')}
          </p>
          <Button asChild variant="secondary" className="mt-4">
            <Link to="/tudastar">{t('knowledge.backHome')}</Link>
          </Button>
        </div>
      </AppShell>
    );
  }

  const draft = draftQuery.data?.status === 200 ? draftQuery.data.body.draft : null;
  // Csak akkor ajánljuk fel, ha ténylegesen ELTÉR a mentett cikktől — egy azonos tartalmú vázlat
  // (pl. mentés utáni maradék) csak zavaró kérdés lenne.
  const draftDiffers =
    draft !== null && (draft.title !== article.title || draft.body !== article.body);

  return (
    <AppShell session={sessionBody}>
      <div className="space-y-6">
        {/* A morzsa a szerkesztő EGYETLEN kivezető útja a tartalom-hierarchiába: innen a cikk, a
            könyv, a szekció és a tudástár is egy kattintás. A cikk-link csak publikált cikknél
            aktív — vázlatnak nincs olvasó-oldala, oda linkelni 404 lenne. */}
        <ArticlePathCrumbs article={article} current={t('editor.edit')} />

        {/* MEGELŐZŐ jelzés: a szerkesztő itt nyílik meg, tehát itt kell megtudnia, hogy a MENTÉS nem
            fog menni — nem a mentés-gomb megnyomása után. (A mentés így is fail-closed: 503-at ad, és
            a hibaüzenet-rendszer ugyanezt az okot mondja.) */}
        {sessionBody?.readOnly === true && <ReadOnlyNotice kind="editing" />}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">{t('editor.title')}</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="sm">
              <Link to="/valtozatok/$articleId" params={{ articleId }}>
                {t('revisions.open')}
              </Link>
            </Button>
            <span className="text-muted-foreground bg-muted rounded-full px-2.5 py-1 text-xs">
              {t(`editor.status.${article.status}`)}
            </span>
          </div>
        </div>

        <LifecycleBar
          article={article}
          onChanged={(updated) => {
            // A státusz-váltás új verziót ad: a szerkesztő OCC-je enélkül azonnal ütközne.
            queryClient.setQueryData(editorKeys.article(articleId), { status: 200, body: updated });
          }}
        />

        {draft && draftDiffers && draftChoice === null && (
          <div className="border-border bg-muted/40 rounded-lg border p-4">
            <p className="font-medium">
              {t('editor.draftBanner.title', {
                time: new Date(draft.savedAt).toLocaleString('hu-HU'),
              })}
            </p>
            <p className="text-muted-foreground mt-1 text-sm">{t('editor.draftBanner.body')}</p>
            <div className="mt-3 flex gap-2">
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  setDraftChoice({ title: draft.title, body: draft.body });
                }}
              >
                {t('editor.draftBanner.load')}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setDraftChoice(undefined);
                  deleteDraft.mutate({ params: { id: articleId }, body: {} });
                }}
              >
                {t('editor.draftBanner.discard')}
              </Button>
            </div>
          </div>
        )}

        {/* A vázlat-döntés a szerkesztő kezdőállapotát dönti el, ezért a komponenst újra kell
            építeni (key) — különben a useState-ek megtartanák a korábbi értéket. */}
        <ArticleEditor
          key={draftChoice ? 'draft' : 'article'}
          article={article}
          {...(draftChoice ? { initial: draftChoice } : {})}
          onSaved={(saved) => {
            if (saved.status !== 'published' || !saved.path) return;
            void navigate({
              to: '/hub/$spaceSlug/$categorySlug/$articleSlug',
              params: {
                spaceSlug: saved.path.spaceSlug,
                categorySlug: saved.path.categorySlug,
                articleSlug: saved.slug,
              },
            });
          }}
        />
      </div>
    </AppShell>
  );
}
